import { Chip, Tooltip, Box } from '@mui/material';
import { useTranslate } from 'react-admin';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { TicketPriority } from '../../types/chat.types';

export interface TicketPriorityBadgeProps {
  priority: TicketPriority | null | undefined;
  size?: 'small' | 'medium';
  iconOnly?: boolean;
  showTooltip?: boolean;
  variant?: 'filled' | 'outlined';
}

const PRIORITY_CONFIG: Record<
  TicketPriority,
  {
    color: 'default' | 'info' | 'warning' | 'error';
    iconFilled: boolean;
  }
> = {
  [TicketPriority.LOW]: {
    color: 'default',
    iconFilled: false,
  },
  [TicketPriority.MEDIUM]: {
    color: 'info',
    iconFilled: false,
  },
  [TicketPriority.HIGH]: {
    color: 'warning',
    iconFilled: true,
  },
  [TicketPriority.URGENT]: {
    color: 'error',
    iconFilled: true,
  },
};

export function TicketPriorityBadge({
  priority,
  size = 'small',
  iconOnly = false,
  showTooltip = true,
  variant = 'outlined',
}: TicketPriorityBadgeProps) {
  const translate = useTranslate();

  // Handle null/undefined priority
  if (!priority) {
    const noPriorityChip = (
      <Chip
        icon={<FlagOutlinedIcon fontSize="small" />}
        label={iconOnly ? undefined : translate('resources.ticket.priority.none')}
        size={size}
        color="default"
        variant="outlined"
        sx={{
          opacity: 0.6,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? 16 : 20,
          },
        }}
      />
    );

    if (showTooltip) {
      return (
        <Tooltip title={translate('resources.ticket.priority.noneDescription')} arrow>
          {noPriorityChip}
        </Tooltip>
      );
    }

    return noPriorityChip;
  }

  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[TicketPriority.MEDIUM];
  const PriorityIcon = config.iconFilled ? FlagIcon : FlagOutlinedIcon;

  const label = translate(`resources.ticket.priority.${priority}`);

  const tooltipText = translate(`resources.ticket.priorityDescription.${priority}`);

  // Icon-only mode for compact display
  if (iconOnly) {
    const iconElement = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: config.color === 'default' ? 'text.secondary' : `${config.color}.main`,
        }}
      >
        <PriorityIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </Box>
    );

    if (showTooltip) {
      return (
        <Tooltip title={tooltipText} arrow placement="top">
          {iconElement}
        </Tooltip>
      );
    }

    return iconElement;
  }

  const chip = (
    <Chip
      icon={<PriorityIcon fontSize="small" />}
      label={label}
      size={size}
      color={config.color}
      variant={variant}
      sx={{
        fontWeight: 500,
        '& .MuiChip-icon': {
          fontSize: size === 'small' ? 16 : 20,
        },
      }}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip title={tooltipText} arrow placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
}

export default TicketPriorityBadge;
